import React, { useEffect, useState } from 'react';
import { clsx } from 'clsx';
import { useTranslation } from 'react-i18next';
import { OrderCreateExtraRefundModal } from '@/types/modal';
import { useOrder, useOrderExtraRefund, useOrderExtraRefundDetail } from '@/hooks/order';
import { Dropdown, Input } from '@/components/molecules/common/Form';
import { ErrorModal, LoadingModal, StandardModal, SuccessModal } from '@/components/molecules/common/Modal';
import { formatCurrency } from '@/utilities/currency';
import styles from './Modal.module.scss';

const View: React.FC<{ modal: OrderCreateExtraRefundModal }> = ({ modal }) => {
  const { t } = useTranslation();
  const { order, onClose } = modal;

  const otherRefundTypeId = 36;
  const [refundType, setRefundType] = useState<string>('');
  const [zendeskId, setZendeskId] = useState<string>('');
  const [refundDesc, setRefundDesc] = useState<string>('');
  const [memo, setMemo] = useState<string>('');
  const [amount, setAmount] = useState<string>('');

  const orderR = useOrder(Number(order.orderId), false);
  const detailR = useOrderExtraRefundDetail(Number(order.orderId));
  const submitR = useOrderExtraRefund(Number(order.orderId));

  useEffect(() => {
    if (detailR.kind === 'Loaded') {
      const refundTypes = detailR.data.types;
      setRefundType(refundTypes[0] ? String(refundTypes[0].id) : '');
    }
  }, [detailR.kind]);

  useEffect(() => {
    if (submitR.kind === 'Completed') {
      orderR.refetch();
    }
  }, [submitR.kind]);

  if (detailR.kind === 'Called') {
    return (
      <LoadingModal
        title={t('modal.order_create_extra_refund.title')}
        onClose={onClose}
      />
    );
  } else if (detailR.kind === 'Error') {
    return (
      <ErrorModal
        title={t('modal.order_create_extra_refund.title')}
        error={detailR.error}
        onClose={onClose}
      />
    );
  } else if (submitR.kind === 'Error') {
    return (
      <ErrorModal
        title={t('modal.order_create_extra_refund.title')}
        error={submitR.error}
        onClose={onClose}
      />
    );
  } else if (submitR.kind === 'Completed') {
    return (
      <SuccessModal
        title={t('modal.order_create_extra_refund.title')}
        onClose={onClose}
      />
    );
  }

  const refundDetail = detailR.data;

  const body = (
    <div className={styles.orderCreateRefund}>
      <div />
      <div className={styles.text1rem}>{`${t('modal.order_create_extra_refund.order')} #${order.orderId}`}</div>
      {refundDetail.hasExtraRefund ? (
        <div className={clsx([styles.orderCreateRefundWarning, styles.text1rem])}>
          {t('modal.order_create_extra_refund.existing')}
        </div>
      ) : (
        <></>
      )}
      <div />
      <div className={styles.orderCreateRefundRow}>
        <div className={styles.text0875rem}>{t('modal.order_create_extra_refund.order_shipping')}</div>
        <div className={styles.text0875rem}>
          {formatCurrency({
            value: refundDetail.shippingFee || 0,
            currency: refundDetail.currency,
          })}
        </div>
      </div>
      <div className={styles.orderCreateRefundRow}>
        <div className={styles.text0875rem}>{t('modal.order_create_extra_refund.order_duty')}</div>
        <div className={styles.text0875rem}>
          {formatCurrency({
            value: refundDetail.dutyAndTaxFee || 0,
            currency: refundDetail.currency,
          })}
        </div>
      </div>
      <div className={styles.orderCreateRefundRow}>
        <div className={clsx([styles.text0875rem, styles.textBold])}>
          {t('modal.order_create_extra_refund.order_remaining')}
        </div>
        <div className={clsx([styles.text0875rem, styles.textBold])}>
          {formatCurrency({
            value: refundDetail.totalRemainValue || 0,
            currency: refundDetail.currency,
          })}
        </div>
      </div>
      <div />
      <div />
      <Dropdown
        required={true}
        label={t('modal.order_create_extra_refund.type')}
        value={refundType}
        onSelect={setRefundType}
        items={refundDetail.types.map((type) => ({
          label: type.name || t('modal.order_refund_types.invalid_type'),
          value: String(type.id),
        }))}
      />
      <div />
      <div />
      {Number(refundType) === otherRefundTypeId ? (
        <>
          <Input
            required={true}
            label={t('modal.order_create_extra_refund.description')}
            value={refundDesc}
            onChange={setRefundDesc}
            multiline={true}
            rows={6}
          />
          <div />
          <div />
        </>
      ) : (
        <></>
      )}
      <Input
        required={true}
        label={t('modal.order_create_extra_refund.zendesk')}
        value={zendeskId}
        onChange={(value: string) => {
          const isValid = /^\d{0,10}$/.test(value);
          if (value === '' || isValid) {
            setZendeskId(value);
          }
        }}
      />
      <div />
      <div />
      <Input
        required={true}
        label={t('modal.order_create_extra_refund.amount')}
        value={amount}
        onChange={(value: string) => {
          const isValid = /^\d+(\.\d{0,2})?$/.test(value);
          if (value === '' || isValid) {
            setAmount(value);
          }
        }}
      />
      <div />
      <div />
      <Input
        required={true}
        label={t('modal.order_create_extra_refund.memo')}
        placeholder={t('modal.order_create_extra_refund.memoPlaceholder')}
        value={memo}
        onChange={setMemo}
        multiline={true}
        rows={6}
        maxLength={1000}
        InputLabelProps={{
          shrink: true,
          style: {
            whiteSpace: 'break-spaces',
          },
        }}
      />
      <div />
      <div />
      <div className={styles.orderCreateRefundFinal}>
        <div className={clsx([styles.text06rem, styles.textGrey])}>
          {t('modal.order_create_extra_refund.refund_amount')}
        </div>
        <div className={styles.text15rem}>
          {formatCurrency({ value: Number(amount), currency: refundDetail.currency })}
        </div>
      </div>
    </div>
  );

  const canUpdate =
    submitR.kind !== 'Loading' &&
    memo != '' &&
    refundType != '' &&
    amount != '' &&
    zendeskId != '' &&
    (Number(refundType) === otherRefundTypeId ? refundDesc != '' : true);

  const onSubmit = () => {
    submitR.mutate({
      type: Number(refundType),
      reason: refundDesc,
      zendeskId: Number(zendeskId),
      comment: memo,
      currency: refundDetail.currency,
      amount: Number(amount),
    });
  };

  return (
    <StandardModal
      title={t('modal.order_create_extra_refund.title')}
      onClose={onClose}
      onSubmit={onSubmit}
      submitLabel={t('common.submit').toUpperCase()}
      submittable={canUpdate}
      body={body}
      containerStyle={{ width: 550 }}
      modalName="order_create_extra_refund"
    />
  );
};

export default View;
